/**
 * Shared top header bar — used by AdminLayout and ReviewerLayout.
 * Clicking the avatar opens a small dropdown with profile link + logout.
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface SiteHeaderProps {
  /** Label shown below the user's display name (e.g. "Adminisztrátor") */
  roleLabel?: string;
}

export default function SiteHeader({ roleLabel = 'Felhasználó' }: SiteHeaderProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const displayName = user?.email?.split('@')[0] ?? 'Felhasználó';
  const initials    = displayName.slice(0, 1).toUpperCase();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="app-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
        <img src="/logo_gymsmkik.svg" style={{ height: 36, width: 'auto' }} alt="GYMSMKIK" />
        <div style={{ borderLeft: '1px solid var(--border)', paddingLeft: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--primary)', lineHeight: 1.2 }}>
            Védjegy Digitalizációs Platform
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 1 }}>
            GYMSM KIK, Védjegy pályázati portál
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--primary)' }}>{displayName}</div>
            <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{roleLabel}</div>
          </div>
          <div ref={menuRef} style={{ position: 'relative' }}>
            <div
              onClick={() => setMenuOpen((o) => !o)}
              title="Fiók"
              style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'var(--primary)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 14, flexShrink: 0, cursor: 'pointer',
                userSelect: 'none',
              }}
            >{initials}</div>

            {menuOpen && (
              <div style={{
                position: 'absolute', top: 44, right: 0,
                background: '#fff', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)',
                minWidth: 200, zIndex: 999, overflow: 'hidden',
              }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--primary)' }}>{displayName}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginTop: 1 }}>{user?.email}</div>
                </div>
                <button
                  onClick={() => { setMenuOpen(false); navigate('/profile'); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    width: '100%', padding: '10px 14px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 13, color: 'var(--primary)', textAlign: 'left',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--muted)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  Profil / Jelszócsere
                </button>
                <div style={{ height: 1, background: 'var(--border)', margin: '0 14px' }} />
                <button
                  onClick={() => { setMenuOpen(false); void logout().then(() => navigate('/login')).catch(() => navigate('/login')); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    width: '100%', padding: '10px 14px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 13, color: '#c0392b', textAlign: 'left',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#fff5f5')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  Kijelentkezés
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
